<template>
  <v-navigation-drawer
    data-cy="global-sidebar"
    v-model="sideNav"
    v-model:mini-variant="sideNav"
    app
    disable-route-watcher
    enable-resize-watcher
    permanent
    :width="width">
    <v-btn
      absolute
      class="v-navigation-drawer-close-button"
      size="large"
      @click="sideNav = !sideNav">
      <v-icon class="v-navigation-drawer-icon"> mdi-menu-left </v-icon>
    </v-btn>

    <div
      id="handle"
      data-cy="side-bar-width-handle"
      class="v-navigation-size-handle" />

    <v-row class="px-2 mt-n8">
      <v-col>
        <div class="base-data-switch-container">
          <div
            data-cy="switch-calculation"
            class="bg-primary base-data-switch"
            @click="switchBaseData"
            :class="{ 'active': !baseData }">
            {{ $t("components.SideBar.Calculation") }}
          </div>
          <div
            data-cy="switch-base-data"
            class="bg-primary base-data-switch base-data"
            @click="switchBaseData"
            :class="{ 'active': baseData }">
            {{ $t("components.SideBar.BaseData") }}
          </div>
        </div>
      </v-col>
    </v-row>

    <v-row class="search-bar px-2">
      <v-col>
        <v-text-field
          v-model="treeSearch"
          density="compact"
          hide-details="auto"
          :label="$t('components.SideBar.Search')"
          persistent-placeholder
          :placeholder="$t('components.SideBar.SearchInTree')"
          prepend-inner-icon="mdi-magnify"
          variant="outlined" />
      </v-col>
    </v-row>

    <TreeNavigation
      :base-data="baseData"
      :search="treeSearch"
      :width="width" />

    <v-btn
      v-if="!spaceStore.readOnly"
      absolute
      class="mt-2 text-capitalize create-folder-button"
      color="primary"
      flat
      prepend-icon="mdi-plus"
      rounded="xl"
      variant="outlined"
      @click="createAction()"
      data-cy="create-folder-button">
      {{ $t("entities.folder.name") }}
    </v-btn>

    <PasteButton
      extra-classes="paste-folder-button"
      :condition="!!mainStore.copyData?.id?.endsWith('folder')"
      data-cy="paste-folder-button" />
  </v-navigation-drawer>

  <v-btn
    v-if="!sideNav"
    absolute
    class="v-navigation-drawer-open-button"
    size="large"
    @click="sideNav = !sideNav">
    <v-icon class="v-navigation-drawer-icon"> mdi-menu-right </v-icon>
  </v-btn>
</template>

<script setup>
import { onMounted, ref } from "vue";
import TreeNavigation from "../TreeView/TreeNavigation.vue";
import { useRoute, useRouter } from "vue-router";
import { useSpaceStore } from "../../stores/spaceStore";
import { useMainStore } from "@/stores/mainStore";
import { watch } from "vue";
import { computed } from "vue";
import { onBeforeMount } from "vue";
import PasteButton from "../Elements/PasteButton.vue";

const sideNav = ref(true);

const treeSearch = ref("");

const router = useRouter();
const route = useRoute();
const spaceStore = useSpaceStore();
const mainStore = useMainStore();
const width = ref(281);

const createAction = () => {
  router.push({
    name: "calculations-folder",
    params: {
      nodeId: spaceStore.createElement("folder")
    }
  });
};

onBeforeMount(() => {
  if (routeMeta.value.baseData) {
    baseData.value = true;
  }
});

onMounted(() => {
  dragElement(document.getElementById("handle"));

  const cachedTreeWidth = localStorage.getItem("tree-width");
  if (cachedTreeWidth) {
    width.value = parseInt(cachedTreeWidth);
  }

  // const baseDataRoutes = ["base-data", "shiftModel", "location", "shift"];
  // console.log(route.path);
  // if (baseDataRoutes.some((a) => route.path.includes(a))) {
  //   baseData.value = true;
  // }
});

const routeMeta = computed(() => route.meta);

const baseData = ref(false);

function switchBaseData() {
  baseData.value = !baseData.value;
}

function dragElement(elmnt) {
  elmnt.onmousedown = dragMouseDown;

  function dragMouseDown(e) {
    e = e || window.event;
    e.preventDefault();
    document.onmouseup = closeDragElement;
    document.onmousemove = elementDrag;
  }

  function elementDrag(e) {
    e = e || window.event;
    e.preventDefault();
    width.value = e.clientX;
    localStorage.setItem("tree-width", width.value);
  }

  function closeDragElement() {
    /* stop moving when mouse button is released: */
    document.onmouseup = null;
    document.onmousemove = null;
  }
}
</script>

<style lang="scss">
.v-navigation-drawer {
  padding-bottom: 0px !important;
}

.v-navigation-drawer--mini-variant,
.v-navigation-drawer {
  overflow: visible !important;
}

.v-navigation-drawer__content {
  padding-top: 48px !important;
  background-color: white;
}

.v-navigation-drawer-close-button {
  position: absolute !important;
  top: 50%;
  padding: 0 !important;
  min-width: 0 !important;
  width: 12px;
  right: 0;
  background-color: #f7f2fa;
  z-index: 1;
}

.v-navigation-size-handle {
  position: absolute !important;
  height: calc(100vh - 48px);
  top: 0;
  padding: 0 !important;
  min-width: 0 !important;
  width: 12px !important;
  right: 0;
  background-color: transparent;
  cursor: col-resize;
  z-index: 0;
}

.v-navigation-drawer-open-button {
  position: absolute;
  top: 50%;
  padding: 0 !important;
  min-width: 0 !important;
  width: 14px;
  left: -3px;
  background-color: #f7f2fa;
}

.search-bar {
  background-color: #f5fbff;
}

.v-navigation-drawer-icon {
  font-size: 36px !important;
}

.create-folder-button {
  position: absolute;
  bottom: 1em;
  left: 1em;
  width: calc(100% - 2em);
}

.paste-folder-button {
  position: absolute;
  bottom: calc(1em + 48px);
  left: 1em;
  width: calc(100% - 2em);
}

.base-data-switch-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
}

.base-data-switch {
  height: 30px;
  line-height: 30px;
  border-radius: 20px;
  text-align: center;
  cursor: pointer;
  opacity: 0.3;
  z-index: 1;

  &.active {
    width: calc(100% + 20px);
    z-index: 2;
    opacity: 1;

    &.base-data {
      margin-left: -20px;
    }
  }
}
</style>
